// frontend/src/modules/onboarding/Spotlight.tsx
//
// The dimmed overlay with a cutout around the anchored element, plus the step
// card. Rendered in a portal so it is never clipped by an ancestor's overflow.

import * as React from 'react';
import { createPortal } from 'react-dom';

export interface SpotlightProps {
  rect: DOMRect;
  title: string;
  body: string;
  index: number;
  total: number;
  onNext: () => void;
  onSkipStep: () => void;
  onClose: () => void;
}

const PAD = 8;
const VIEWPORT_MARGIN = 16;

export function Spotlight({ rect, title, body, index, total, onNext, onSkipStep, onClose }: SpotlightProps) {
  const cardRef = React.useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = React.useState(0);

  // Focus the card on every stop so keyboard users follow the tour, and the
  // step text is announced.
  React.useEffect(() => { cardRef.current?.focus(); }, [index]);

  // Measure the card so we know whether it fits below the anchor. jsdom
  // reports 0 for all layout metrics, so in tests this simply never flips —
  // that is fine, position is a real-browser concern.
  React.useLayoutEffect(() => {
    if (cardRef.current) setCardHeight(cardRef.current.getBoundingClientRect().height);
  }, [index, title, body]);

  const isLast = index === total - 1;

  // An anchor low in the viewport must not push the card off-screen: dimming
  // the page while Next/Skip are unreachable is the closest thing in this
  // module to a trap. Clamp within the viewport, and flip the card above the
  // anchor when there isn't room below.
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
  const spaceBelow = viewportHeight - rect.bottom - PAD;
  const spaceAbove = rect.top - PAD;
  const flipAbove = cardHeight > 0 && spaceBelow < cardHeight && spaceAbove > spaceBelow;

  let top: number;
  if (flipAbove) {
    top = Math.max(VIEWPORT_MARGIN, rect.top - PAD - cardHeight);
  } else {
    top = rect.bottom + PAD;
    if (cardHeight > 0) {
      top = Math.min(top, Math.max(VIEWPORT_MARGIN, viewportHeight - cardHeight - VIEWPORT_MARGIN));
    }
  }

  // `aria-modal="true"` tells assistive tech the rest of the page is inert.
  // That is only true if Tab actually can't leave the card, so trap focus
  // within it while it's open.
  const onCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !cardRef.current) return;
    const focusables = cardRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100]">
      {/* Dimmer with a transparent hole punched over the anchor. */}
      <div
        onClick={onClose}
        className="absolute inset-0 bg-ink/60"
        style={{
          clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0,
            ${rect.left - PAD}px ${rect.top - PAD}px,
            ${rect.left - PAD}px ${rect.bottom + PAD}px,
            ${rect.right + PAD}px ${rect.bottom + PAD}px,
            ${rect.right + PAD}px ${rect.top - PAD}px,
            ${rect.left - PAD}px ${rect.top - PAD}px)`,
        }}
      />
      {/* Highlight ring around the anchor. */}
      <div
        aria-hidden
        className="absolute rounded-xl ring-2 ring-coral pointer-events-none"
        style={{
          left: rect.left - PAD, top: rect.top - PAD,
          width: rect.width + PAD * 2, height: rect.height + PAD * 2,
        }}
      />
      <div
        ref={cardRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={onCardKeyDown}
        className="absolute max-w-sm rounded-2xl bg-background ring-1 ring-border shadow-xl p-5 outline-none"
        style={{ left: Math.max(VIEWPORT_MARGIN, rect.left - PAD), top }}
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Step {index + 1} of {total}
        </p>
        <h3 className="mt-1 font-semibold text-[15px]">{title}</h3>
        <p aria-live="polite" className="mt-1.5 text-[13px] text-muted-foreground leading-relaxed">{body}</p>
        <div className="mt-4 flex items-center justify-between gap-3">
          <button type="button" onClick={onSkipStep} className="text-[12.5px] text-muted-foreground hover:text-foreground">
            Skip this step
          </button>
          <button
            type="button"
            onClick={onNext}
            className="rounded-full h-9 px-4 text-[13px] font-semibold bg-foreground text-background"
          >
            {isLast ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
